/**
 * 고객사 자동완성 컴포넌트
 * One Source Multi Use - 고객사 검색이 필요한 모든 곳에서 재사용
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from './Input';
import { Customer } from '@/types';

interface CustomerAutocompleteProps {
  customers: Customer[];
  value?: number;
  onChange: (customerId: number | undefined) => void;
  placeholder?: string;
  label?: string;
  maxSuggestions?: number;
}

export const CustomerAutocomplete: React.FC<CustomerAutocompleteProps> = ({
  customers,
  value,
  onChange,
  placeholder = '고객사 코드 또는 이름 검색',
  label,
  maxSuggestions = 10,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // 고객사 ID에 해당하는 고객사 정보 찾기
  const getCustomerById = (id: number | undefined): Customer | undefined => {
    if (!id) return undefined;
    return customers.find(c => c.id === id);
  };

  // 고객사 검색 필터링
  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase().trim();
    return customers.filter(customer => {
      const code = customer.customer_code?.toLowerCase() || '';
      const name = customer.customer_name?.toLowerCase() || '';
      const alias = customer.alias?.toLowerCase() || '';
      return code.includes(query) || name.includes(query) || alias.includes(query);
    }).slice(0, maxSuggestions);
  }, [searchQuery, customers, maxSuggestions]);

  // 고객사 선택 핸들러
  const handleCustomerSelect = (selectedCustomer: Customer) => {
    onChange(selectedCustomer.id);
    setSearchQuery(`${selectedCustomer.customer_code} (${selectedCustomer.customer_name})`);
    setShowSuggestions(false);
  };

  // 고객사 입력 변경 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchQuery(inputValue);

    // 숫자만 입력된 경우 ID로 직접 설정
    if (/^\d+$/.test(inputValue.trim())) {
      const customerId = parseInt(inputValue.trim(), 10);
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        onChange(customer.id);
        setSearchQuery(`${customer.customer_code} (${customer.customer_name})`);
        setShowSuggestions(false);
        return;
      }
    }

    // 입력값이 있으면 자동완성 표시
    if (inputValue.trim()) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      onChange(undefined);
    }
  };

  // 외부 클릭 시 자동완성 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // value prop이 변경되면 검색어 업데이트
  useEffect(() => {
    if (value) {
      const customer = getCustomerById(value);
      if (customer) {
        setSearchQuery(`${customer.customer_code} (${customer.customer_name})`);
      } else {
        setSearchQuery(String(value));
      }
    } else {
      setSearchQuery('');
    }
  }, [value, customers]);

  return (
    <div className="relative">
      {label && (
        <label className="text-sm font-medium mb-1 block">{label}</label>
      )}
      <Input
        ref={inputRef}
        type="text"
        value={searchQuery}
        onChange={handleInputChange}
        onFocus={() => {
          if (searchQuery.trim() && filteredCustomers.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        className="w-full"
      />
      {showSuggestions && filteredCustomers.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredCustomers.map((customer) => (
            <div
              key={customer.id}
              onClick={() => handleCustomerSelect(customer)}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-200 last:border-b-0"
            >
              <div className="font-medium text-sm">
                {customer.customer_code} ({customer.customer_name})
              </div>
              {customer.alias && (
                <div className="text-xs text-gray-500">별칭: {customer.alias}</div>
              )}
              <div className="text-xs text-gray-400">ID: {customer.id}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerAutocomplete;
